import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Trophy, 
  Users, 
  Calendar, 
  Play,
  Check,
  Vote,
  Crown,
  Swords,
  ChevronRight,
  Loader2,
  User,
  Clock
} from 'lucide-react';


function MatchCard({ match, battleId, onVote, userVotes, isOrganizer, onActivate, onComplete }) {
  const [voting, setVoting] = useState(null);
  const hasVoted = userVotes?.some(v => v.match_id === match.match_id);
  const userVote = userVotes?.find(v => v.match_id === match.match_id);
  
  const totalVotes = match.votes_1 + match.votes_2;
  const p1Percent = totalVotes > 0 ? Math.round(match.votes_1 / totalVotes * 100) : 50;
  const p2Percent = totalVotes > 0 ? Math.round(match.votes_2 / totalVotes * 100) : 50;

  const handleVote = async (voteFor) => {
    setVoting(voteFor);
    await onVote(match.match_id, voteFor);
    setVoting(null);
  };

  // Check if it's a bye match
  const isBye = !match.participant_1 || !match.participant_2;
  
  if (isBye && match.status === 'completed') {
    const advancingPlayer = match.participant_1_info || match.participant_2_info;
    return (
      <Card className="p-3 bg-card/30 border border-border">
        <div className="text-center text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{advancingPlayer?.stage_name || 'TBD'}</span>
          <Badge variant="outline" className="ml-2 text-xs">BYE</Badge>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-4 ${match.status === 'active' ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}>
      {/* Match Header */}
      <div className="flex items-center justify-between mb-3">
        <Badge variant={match.status === 'active' ? 'default' : 'outline'} className="text-xs">
          {match.status === 'pending' && 'Upcoming'}
          {match.status === 'active' && '🔴 LIVE'}
          {match.status === 'completed' && 'Completed'}
        </Badge>
        {match.status === 'active' && totalVotes > 0 && (
          <span className="text-xs text-muted-foreground">{totalVotes} votes</span>
        )}
      </div>

      {/* Participants */}
      <div className="space-y-3">
        {/* Participant 1 */}
        <div className={`flex items-center gap-3 p-2 rounded-lg ${
          match.winner === match.participant_1 ? 'bg-green-500/10 border border-green-500/30' : 
          match.status === 'active' ? 'bg-muted/30' : ''
        }`}>
          <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
            {match.participant_1_info?.avatar_url ? (
              <img src={match.participant_1_info.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {match.participant_1_info?.stage_name || 'TBD'}
            </p>
            {match.status === 'active' && (
              <Progress value={p1Percent} className="h-1.5 mt-1" />
            )}
          </div>
          {match.status === 'active' && (
            <div className="text-right">
              <span className="text-sm font-bold">{p1Percent}%</span>
            </div>
          )}
          {match.winner === match.participant_1 && (
            <Crown className="w-5 h-5 text-amber-400" />
          )}
          {match.status === 'active' && !hasVoted && (
            <Button 
              size="sm" 
              variant={userVote?.voted_for === match.participant_1 ? 'default' : 'outline'}
              onClick={() => handleVote('participant_1')}
              disabled={voting !== null}
            >
              {voting === 'participant_1' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vote'}
            </Button>
          )}
        </div>

        {/* VS Divider */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-muted/50" />
          <span className="text-xs text-muted-foreground">VS</span>
          <div className="flex-1 h-px bg-muted/50" />
        </div>

        {/* Participant 2 */}
        <div className={`flex items-center gap-3 p-2 rounded-lg ${
          match.winner === match.participant_2 ? 'bg-green-500/10 border border-green-500/30' : 
          match.status === 'active' ? 'bg-muted/30' : ''
        }`}>
          <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
            {match.participant_2_info?.avatar_url ? (
              <img src={match.participant_2_info.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {match.participant_2_info?.stage_name || 'TBD'}
            </p>
            {match.status === 'active' && (
              <Progress value={p2Percent} className="h-1.5 mt-1" />
            )}
          </div>
          {match.status === 'active' && (
            <div className="text-right">
              <span className="text-sm font-bold">{p2Percent}%</span>
            </div>
          )}
          {match.winner === match.participant_2 && (
            <Crown className="w-5 h-5 text-amber-400" />
          )}
          {match.status === 'active' && !hasVoted && (
            <Button 
              size="sm"
              variant={userVote?.voted_for === match.participant_2 ? 'default' : 'outline'}
              onClick={() => handleVote('participant_2')}
              disabled={voting !== null}
            >
              {voting === 'participant_2' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vote'}
            </Button>
          )}
        </div>
      </div>

      {/* Voted indicator */}
      {hasVoted && (
        <div className="mt-3 text-center text-xs text-muted-foreground">
          <Check className="w-3 h-3 inline mr-1" />
          You voted for {userVote?.voted_for === match.participant_1 ? match.participant_1_info?.stage_name : match.participant_2_info?.stage_name}
        </div>
      )}

      {/* Organizer Controls */}
      {isOrganizer && match.status === 'pending' && match.participant_1 && match.participant_2 && (
        <Button 
          size="sm" 
          className="w-full mt-3"
          onClick={() => onActivate(match.match_id)}
        >
          <Play className="w-4 h-4 mr-2" />
          Start Voting
        </Button>
      )}
      {isOrganizer && match.status === 'active' && (
        <Button 
          size="sm" 
          variant="outline"
          className="w-full mt-3"
          onClick={() => onComplete(match.match_id)}
        >
          <Check className="w-4 h-4 mr-2" />
          End Voting & Declare Winner
        </Button>
      )}
    </Card>
  );
}

export default function BattleDetailPage() {
  const { battleId } = useParams();
  const { isAuthenticated, getAuthHeaders, user } = useAuth();
  
  const [battle, setBattle] = useState(null);
  const [bracket, setBracket] = useState(null);
  const [userVotes, setUserVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    fetchBattle();
    fetchBracket();
    if (isAuthenticated) {
      fetchMyVotes();
    }
  }, [battleId, isAuthenticated]);

  const fetchBattle = async () => {
    try {
      const response = await fetch(`${API}/battles/${battleId}`);
      if (response.ok) {
        setBattle(await response.json());
      }
    } catch (error) {
      console.error('Error fetching battle:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBracket = async () => {
    try {
      const response = await fetch(`${API}/battles/${battleId}/bracket`);
      if (response.ok) {
        const data = await response.json();
        setBracket(data.bracket);
      }
    } catch (error) {
      console.error('Error fetching bracket:', error);
    }
  };

  const fetchMyVotes = async () => {
    try {
      const response = await fetch(`${API}/battles/${battleId}/my-votes`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUserVotes(data.votes || []);
      }
    } catch (error) {
      console.error('Error fetching votes:', error);
    }
  };

  const handleStartBattle = async () => {
    setStarting(true);
    try {
      const response = await fetch(`${API}/battles/${battleId}/start`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Battle started!');
        fetchBattle();
        fetchBracket();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start battle');
      }
    } catch (error) {
      toast.error('Failed to start battle');
    } finally {
      setStarting(false);
    }
  };

  const handleVote = async (matchId, voteFor) => {
    try {
      const response = await fetch(`${API}/battles/${battleId}/matches/${matchId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ vote_for: voteFor })
      });
      if (response.ok) {
        toast.success('Vote recorded!');
        fetchBracket();
        fetchMyVotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to vote');
      }
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleActivateMatch = async (matchId) => {
    try {
      const response = await fetch(`${API}/battles/${battleId}/matches/${matchId}/activate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Match activated for voting!');
        fetchBracket();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to activate match');
      }
    } catch (error) {
      toast.error('Failed to activate match');
    }
  };

  const handleCompleteMatch = async (matchId) => {
    try {
      const response = await fetch(`${API}/battles/${battleId}/matches/${matchId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({})
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Winner declared!`);
        fetchBattle();
        fetchBracket();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to complete match');
      }
    } catch (error) {
      toast.error('Failed to complete match');
    }
  };

  const isOrganizer = user?.user_id === battle?.organizer_id || user?.role === 'admin' || user?.role === 'owner';

  const getRoundName = (roundNum, totalRounds) => {
    if (roundNum === totalRounds) return 'Finals';
    if (roundNum === totalRounds - 1) return 'Semi-Finals';
    if (roundNum === totalRounds - 2) return 'Quarter-Finals';
    return `Round ${roundNum}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!battle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Battle not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="battle-detail-page">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={battle.status === 'active' ? 'default' : 'outline'}>
                {battle.status}
              </Badge>
              <Badge variant="outline">{battle.battle_mode}</Badge>
            </div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Swords className="w-8 h-8 text-primary" />
              {battle.title}
            </h1>
            {battle.description && (
              <p className="text-muted-foreground mt-2">{battle.description}</p>
            )}
          </div>

          {/* Stats */}
          <div className="flex gap-4">
            <Card className="p-4 text-center">
              <Users className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-2xl font-bold">{battle.participants_count}</p>
              <p className="text-xs text-muted-foreground">of {battle.bracket_size}</p>
            </Card>
            <Card className="p-4 text-center">
              <Trophy className="w-5 h-5 mx-auto mb-1 text-amber-400" />
              <p className="text-2xl font-bold">${battle.prize_pool}</p>
              <p className="text-xs text-muted-foreground">Prize Pool</p>
            </Card>
          </div>
        </div>

        {/* Winner Banner */}
        {battle.winner_id && (
          <Card className="p-6 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 text-center">
            <Crown className="w-12 h-12 mx-auto mb-3 text-amber-400" />
            <h2 className="text-2xl font-bold mb-2">Battle Champion!</h2>
            <p className="text-muted-foreground">Winner: {battle.winner_id}</p>
          </Card>
        )}

        {/* Organizer Controls */}
        {isOrganizer && battle.status === 'open' && (
          <Card className="p-4 bg-primary/10 border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Ready to start?</p>
                <p className="text-sm text-muted-foreground">
                  {battle.participants_count} participants entered
                </p>
              </div>
              <Button 
                onClick={handleStartBattle} 
                disabled={starting || battle.participants_count < 2}
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Generate Bracket & Start
              </Button>
            </div>
          </Card>
        )}

        {/* Bracket */}
        {bracket ? (
          <div className="space-y-8">
            <h2 className="text-xl font-bold">Tournament Bracket</h2>
            
            <div className="flex gap-6 overflow-x-auto pb-4">
              {bracket.rounds?.map((round, idx) => (
                <div key={round.round_number} className="flex-shrink-0 w-72">
                  <h3 className="text-sm font-medium text-muted-foreground mb-4 text-center">
                    {getRoundName(round.round_number, bracket.total_rounds)}
                  </h3>
                  <div className="space-y-4">
                    {round.matches.map((match) => (
                      <MatchCard
                        key={match.match_id}
                        match={match}
                        battleId={battleId}
                        onVote={handleVote}
                        userVotes={userVotes}
                        isOrganizer={isOrganizer}
                        onActivate={handleActivateMatch}
                        onComplete={handleCompleteMatch}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : battle.status === 'open' ? (
          <Card className="p-8 text-center">
            <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-bold mb-2">Waiting for Battle to Start</h3>
            <p className="text-muted-foreground">
              The bracket will be generated when the organizer starts the battle.
            </p>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
